import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause, RotateCcw, Check, Mic, Square, Volume2, Headphones } from 'lucide-react';
import { splitSentences, estimateReadingTime } from '../../utils/text';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface PageData {
  imageUrl?: string;
  content?: string;
  pageNumber?: number;
}

interface ReadAlongModeProps {
  page: PageData;
  currentIndex: number;
  totalPages: number;
  storyId?: string;
  onNextPage: () => void;
  onPrevPage: () => void;
  onExit: () => void;
  onModeChange?: (mode: 'auto') => void;
}

export const ReadAlongMode: React.FC<ReadAlongModeProps> = ({
  page,
  currentIndex,
  totalPages,
  storyId,
  onNextPage,
  onPrevPage,
  onExit,
  onModeChange
}) => {
  const ENABLE_RECORDING = true;
  const sentences = splitSentences(page.content || '');
  const [sentenceIndex, setSentenceIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  // 录音状态
  const [isRecording, setIsRecording] = useState(false);
  const [isUploadingRecording, setIsUploadingRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [isPlayingRecording, setIsPlayingRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingAudioRef = useRef<HTMLAudioElement | null>(null);

  // 页面切换时重置
  useEffect(() => {
    setSentenceIndex(0);
    setIsAutoPlaying(false);
    setIsCompleted(false);
    setRecordingUrl(null);
    setIsPlayingRecording(false);
    if (autoPlayRef.current) {
      clearTimeout(autoPlayRef.current);
      autoPlayRef.current = null;
    }
    if (recordingAudioRef.current) {
      recordingAudioRef.current.pause();
      recordingAudioRef.current = null;
    }
    // 切换页面时强制停止录音
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream?.getTracks().forEach(track => track.stop());
      } catch {
        // ignore
      }
      setIsRecording(false);
    }
  }, [currentIndex]);

  // 加载本页已有录音
  useEffect(() => {
    if (!storyId || !page.pageNumber) return;
    const fetchRecording = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `${API_BASE_URL}/stories/${storyId}/recordings?pageNumber=${page.pageNumber}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        const data = await res.json();
        if (data.success && data.data.recordingUrl) {
          setRecordingUrl(data.data.recordingUrl);
        }
      } catch (err) {
        // 静默失败
      }
    };
    fetchRecording();
  }, [currentIndex, storyId, page.pageNumber]);

  // 自动推进逻辑
  useEffect(() => {
    if (!isAutoPlaying || sentenceIndex >= sentences.length) return;

    const currentSentence = sentences[sentenceIndex];
    const duration = estimateReadingTime(currentSentence);

    autoPlayRef.current = setTimeout(() => {
      if (sentenceIndex < sentences.length - 1) {
        setSentenceIndex(prev => prev + 1);
      } else {
        setIsAutoPlaying(false);
        setIsCompleted(true);
      }
    }, duration);

    return () => {
      if (autoPlayRef.current) {
        clearTimeout(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, sentenceIndex, sentences]);

  // 手动切换下一句
  const nextSentence = useCallback(() => {
    if (sentenceIndex < sentences.length - 1) {
      setSentenceIndex(prev => prev + 1);
      setIsCompleted(false);
    } else {
      setIsCompleted(true);
      setIsAutoPlaying(false);
    }
  }, [sentenceIndex, sentences.length]);

  // 跳转到指定句
  const jumpToSentence = (index: number) => {
    setSentenceIndex(index);
    setIsCompleted(false);
    if (isAutoPlaying) setIsAutoPlaying(false);
  };

  // 重新开始本页
  const resetPage = () => {
    setSentenceIndex(0);
    setIsCompleted(false);
    setIsAutoPlaying(true);
  };

  // 切换自动播放
  const toggleAutoPlay = () => {
    if (isCompleted) {
      resetPage();
    } else {
      setIsAutoPlaying(!isAutoPlaying);
    }
  };

  // 检测录音兼容性
  const checkRecordingSupport = useCallback((): { supported: boolean; reason?: string } => {
    if (typeof navigator === 'undefined') return { supported: false, reason: '浏览器不支持' };
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return { supported: false, reason: '当前浏览器不支持录音，请在系统浏览器中打开' };
    }
    if (typeof MediaRecorder === 'undefined') {
      return { supported: false, reason: '当前浏览器不支持录音功能' };
    }
    // 检测至少有一种支持的 mimeType
    const supportedTypes = ['audio/webm', 'audio/mp4', 'audio/webm;codecs=opus', 'audio/mp4;codecs=mp4a'];
    const hasSupportedType = supportedTypes.some(t => MediaRecorder.isTypeSupported(t));
    if (!hasSupportedType) {
      return { supported: false, reason: '当前浏览器不支持音频录制格式' };
    }
    return { supported: true };
  }, []);

  // 录音控制（增强兼容性）
  const startRecording = async () => {
    const support = checkRecordingSupport();
    if (!support.supported) {
      alert(support.reason);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // 按优先级尝试不同格式（iOS Safari 优先 audio/mp4，安卓优先 audio/webm）
      const mimeTypeCandidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4;codecs=mp4a',
        'audio/mp4',
        'audio/aac',
      ];
      const mimeType = mimeTypeCandidates.find(t => MediaRecorder.isTypeSupported(t)) || undefined;

      const mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const finalMimeType = mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: finalMimeType });
        uploadRecording(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        alert('录音出错，请重试');
        setIsRecording(false);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(1000); // 每1秒收集一次数据，防止某些浏览器丢失数据
      setIsRecording(true);
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        alert('麦克风权限被拒绝，请在浏览器设置中允许访问麦克风');
      } else if (err.name === 'NotFoundError') {
        alert('未找到麦克风设备');
      } else {
        alert('无法访问麦克风，请检查权限设置');
      }
      console.error('录音启动失败:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const uploadRecording = async (blob: Blob) => {
    if (!storyId || !page.pageNumber) return;
    setIsUploadingRecording(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('audio', blob, `page-${page.pageNumber}.webm`);
      formData.append('pageNumber', String(page.pageNumber));

      const res = await fetch(`${API_BASE_URL}/stories/${storyId}/recordings`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setRecordingUrl(data.data.recordingUrl);
      } else {
        alert(data.error || '上传录音失败');
      }
    } catch (err) {
      console.error('上传录音失败:', err);
      alert('上传录音失败');
    } finally {
      setIsUploadingRecording(false);
    }
  };

  const playRecording = () => {
    if (!recordingUrl) return;
    if (!recordingAudioRef.current) {
      recordingAudioRef.current = new Audio(recordingUrl);
      recordingAudioRef.current.onended = () => setIsPlayingRecording(false);
    }
    if (isPlayingRecording) {
      recordingAudioRef.current.pause();
      recordingAudioRef.current.currentTime = 0;
      setIsPlayingRecording(false);
    } else {
      recordingAudioRef.current.play().catch(console.error);
      setIsPlayingRecording(true);
    }
  };

  // 处理键盘事件
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSentence();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (sentenceIndex > 0) {
          setSentenceIndex(prev => prev - 1);
          setIsCompleted(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sentenceIndex, sentences.length, nextSentence]);

  return (
    <div className="h-dvh flex flex-col bg-gradient-to-b from-amber-50 via-white to-white overflow-hidden">
      {/* 顶部控制栏 */}
      <div className="h-[6%] md:h-[5%] flex justify-between items-center px-4 md:px-6 bg-white/90 backdrop-blur-sm shadow-sm z-20">
        <div className="flex items-center gap-2 md:gap-3">
          <button
            onClick={onExit}
            className="p-2 rounded-full hover:bg-gray-100 transition"
          >
            <span className="text-gray-600 text-xl">✕</span>
          </button>
          <span className="text-sm md:text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
            {page.pageNumber} / {totalPages}
          </span>
          {sentences.length > 1 && (
            <span className="text-xs text-gray-400 hidden sm:inline">
              第 {sentenceIndex + 1}/{sentences.length} 句
            </span>
          )}
        </div>

        {/* 桌面端按钮组 */}
        <div className="hidden lg:flex items-center gap-2">
          {/* 切换到自动播放模式 */}
          {onModeChange && (
            <button
              onClick={() => onModeChange('auto')}
              className="flex items-center gap-1 px-2 md:px-3 py-1.5 rounded-full text-xs md:text-sm font-medium bg-gray-800 text-white hover:bg-gray-700 transition"
            >
              <Headphones className="w-3 h-3" />
              <span className="hidden sm:inline">听读</span>
            </button>
          )}

          {/* 录音按钮 */}
          {ENABLE_RECORDING && (isRecording ? (
            <button
              onClick={stopRecording}
              className="flex items-center gap-1 px-2 md:px-3 py-1.5 rounded-full text-xs md:text-sm font-medium bg-red-500 text-white shadow-md animate-pulse"
            >
              <Square className="w-3 h-3 fill-current" />
              <span className="hidden sm:inline">停止</span>
            </button>
          ) : isUploadingRecording ? (
            <button
              disabled
              className="flex items-center gap-1 px-2 md:px-3 py-1.5 rounded-full text-xs md:text-sm font-medium bg-gray-100 text-gray-400"
            >
              <span className="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
              <span className="hidden sm:inline">上传中</span>
            </button>
          ) : recordingUrl ? (
            <button
              onClick={playRecording}
              className={`flex items-center gap-1 px-2 md:px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-all ${
                isPlayingRecording
                  ? 'bg-amber-500 text-white shadow-md'
                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              }`}
            >
              {isPlayingRecording ? <Volume2 className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              <span className="hidden sm:inline">{isPlayingRecording ? '播放中' : '我的录音'}</span>
            </button>
          ) : (
            <button
              onClick={startRecording}
              className="flex items-center gap-1 px-2 md:px-3 py-1.5 rounded-full text-xs md:text-sm font-medium bg-rose-100 text-rose-600 hover:bg-rose-200 transition-all"
            >
              <Mic className="w-3 h-3" />
              <span className="hidden sm:inline">跟读录音</span>
            </button>
          ))}

          <button
            onClick={toggleAutoPlay}
            className={`flex items-center gap-1 px-2 md:px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-all ${
              isAutoPlaying
                ? 'bg-blue-500 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {isAutoPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            <span className="hidden sm:inline">{isAutoPlaying ? '暂停' : '自动跟读'}</span>
          </button>

          <button
            onClick={resetPage}
            className="p-1.5 md:px-3 md:py-1.5 bg-green-500 text-white rounded-full hover:bg-green-600 transition shadow-md flex items-center gap-1"
            title="重读本页"
          >
            <RotateCcw className="w-3 h-3" />
            <span className="hidden sm:inline text-xs md:text-sm font-medium">重读</span>
          </button>
        </div>
      </div>

      {/* 图片区域 */}
      <div className="h-[42%] md:h-[50%] flex items-center justify-center px-4 md:px-8 py-2 md:py-4 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            className="h-full aspect-[4/3] relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4 }}
          >
            <img
              src={page.imageUrl || ''}
              alt={`Page ${page.pageNumber}`}
              className="h-full w-full object-contain rounded-xl md:rounded-2xl shadow-2xl border-4 border-white bg-white"
            />
          </motion.div>
        </AnimatePresence>

        {/* 页面切换箭头 */}
        <button
          onClick={onPrevPage}
          disabled={currentIndex === 0}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:scale-110 transition disabled:opacity-30 disabled:hover:scale-100 z-10"
        >
          <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
        </button>
        <button
          onClick={onNextPage}
          disabled={currentIndex === totalPages - 1}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:scale-110 transition disabled:opacity-30 disabled:hover:scale-100 z-10"
        >
          <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
        </button>
      </div>

      {/* 文字区域 - 核心高亮逻辑 */}
      <div
        className="h-[42%] md:h-[45%] flex flex-col items-center px-4 md:px-8 pb-2 md:pb-4 overflow-hidden"
        onClick={() => !isCompleted && nextSentence()}
      >
        <div className="flex-1 flex flex-col justify-center items-center w-full max-w-3xl mx-auto space-y-2 md:space-y-3 overflow-y-auto py-2 px-4 md:px-8">
          {sentences.length === 0 ? (
            <p className="text-gray-400 text-center">暂无文本内容</p>
          ) : (
            sentences.map((sentence, index) => {
              const isActive = index === sentenceIndex;
              const isPast = index < sentenceIndex;

              return (
                <motion.div
                  key={`${currentIndex}-${index}`}
                  initial={false}
                  animate={{
                    scale: isActive ? 1.03 : 1,
                    opacity: isActive ? 1 : (isPast ? 0.5 : 0.35),
                  }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    jumpToSentence(index);
                  }}
                  className={`
                    relative px-4 md:px-6 py-3 md:py-4 rounded-xl md:rounded-2xl cursor-pointer transition-all duration-300 w-full flex justify-center items-center
                    ${isActive
                      ? 'bg-blue-500 shadow-lg md:shadow-xl'
                      : 'bg-white/60 hover:bg-white/80'
                    }
                  `}
                >
                  <p className={`
                    text-center font-semibold leading-relaxed md:leading-loose tracking-wide transition-all duration-300 max-w-4xl
                    ${isActive
                      ? 'text-white text-lg md:text-2xl lg:text-3xl'
                      : 'text-gray-600 text-base md:text-xl lg:text-2xl'
                    }
                    ${isPast ? 'line-through decoration-gray-300 decoration-2' : ''}
                  `}>
                    {sentence.split(' ').map((word, wIdx, arr) => (
                      <span key={wIdx}>
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            const cleanWord = word.replace(/[.,!?;:"'()]/g, '').trim().toLowerCase();
                            if (!cleanWord) return;
                            const utterance = new SpeechSynthesisUtterance(cleanWord);
                            utterance.lang = 'en-US';
                            utterance.rate = 0.8;
                            window.speechSynthesis.cancel();
                            window.speechSynthesis.speak(utterance);
                          }}
                          className="cursor-pointer hover:bg-white/20 rounded px-0.5 transition-colors"
                          title="点击听发音"
                        >
                          {word}
                        </span>
                        {wIdx < arr.length - 1 ? ' ' : ''}
                      </span>
                    ))}
                  </p>

                  {/* 当前句播放指示器（音波动画） */}
                  {isActive && (
                    <div className="flex justify-center mt-2 md:mt-3 space-x-1 h-3 md:h-4">
                      {[0, 1, 2, 3].map((i) => (
                        <motion.div
                          key={i}
                          className="w-1 md:w-1.5 bg-white/90 rounded-full"
                          animate={{
                            height: [4, 12, 4],
                            opacity: [0.5, 1, 0.5]
                          }}
                          transition={{
                            duration: 0.6,
                            repeat: Infinity,
                            delay: i * 0.15,
                            ease: "easeInOut"
                          }}
                        />
                      ))}
                    </div>
                  )}

                  {/* 完成标记 */}
                  {isPast && (
                    <div className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2">
                      <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
                    </div>
                  )}
                </motion.div>
              );
            })
          )}

          {/* 完成提示 */}
          <AnimatePresence>
            {isCompleted && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-center py-3 md:py-4 bg-green-50 rounded-2xl px-6"
                onClick={(e) => e.stopPropagation()}
              >
                <p className="text-green-600 font-bold mb-3 text-base md:text-lg">🎉 本页读完！</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={resetPage}
                    className="px-4 md:px-6 py-2 bg-green-500 text-white rounded-full font-medium hover:bg-green-600 transition text-sm md:text-base"
                  >
                    再读一遍
                  </button>
                  {currentIndex < totalPages - 1 && (
                    <button
                      onClick={onNextPage}
                      className="px-4 md:px-6 py-2 bg-blue-500 text-white rounded-full font-medium hover:bg-blue-600 transition text-sm md:text-base"
                    >
                      下一页 →
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* 底部提示 */}
        <div className="mt-2 flex justify-center items-center gap-3 text-gray-400 text-xs">
          <span>👆 点击句子跳转</span>
          <span className="hidden sm:inline">•</span>
          <span className="hidden sm:inline">点击空白处下一句</span>
        </div>
      </div>

      {/* 移动端底部操作栏（大按钮，方便孩子操作） */}
      <div className="lg:hidden flex-none h-[10%] bg-white border-t border-gray-100 px-4 py-1 flex items-center justify-around gap-2 z-20">
        {/* 听读切换 */}
        {onModeChange && (
          <button
            onClick={() => onModeChange('auto')}
            className="flex flex-col items-center justify-center gap-1 w-16 h-14 rounded-2xl bg-gray-800 text-white active:bg-gray-700 transition"
          >
            <Headphones className="w-5 h-5" />
            <span className="text-[10px] font-medium">听读</span>
          </button>
        )}

        {/* 录音按钮 */}
        {ENABLE_RECORDING && (isRecording ? (
          <button
            onClick={stopRecording}
            className="flex flex-col items-center justify-center gap-1 w-16 h-14 rounded-2xl bg-red-500 text-white shadow-md animate-pulse active:bg-red-600 transition"
          >
            <Square className="w-5 h-5 fill-current" />
            <span className="text-[10px] font-medium">停止</span>
          </button>
        ) : isUploadingRecording ? (
          <button
            disabled
            className="flex flex-col items-center justify-center gap-1 w-16 h-14 rounded-2xl bg-gray-100 text-gray-400"
          >
            <span className="w-5 h-5 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
            <span className="text-[10px] font-medium">上传中</span>
          </button>
        ) : recordingUrl ? (
          <button
            onClick={playRecording}
            className={`flex flex-col items-center justify-center gap-1 w-16 h-14 rounded-2xl transition-all active:scale-95 ${
              isPlayingRecording
                ? 'bg-amber-500 text-white shadow-md'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            {isPlayingRecording ? <Volume2 className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            <span className="text-[10px] font-medium">{isPlayingRecording ? '播放中' : '我的录音'}</span>
          </button>
        ) : (
          <button
            onClick={startRecording}
            className="flex flex-col items-center justify-center gap-1 w-16 h-14 rounded-2xl bg-rose-500 text-white active:bg-rose-600 transition shadow-sm"
          >
            <Mic className="w-5 h-5" />
            <span className="text-[10px] font-medium">录音</span>
          </button>
        ))}

        {/* 自动跟读 */}
        <button
          onClick={toggleAutoPlay}
          className={`flex flex-col items-center justify-center gap-1 w-16 h-14 rounded-2xl transition-all active:scale-95 ${
            isAutoPlaying
              ? 'bg-blue-500 text-white shadow-md'
              : 'bg-blue-50 text-blue-600'
          }`}
        >
          {isAutoPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          <span className="text-[10px] font-medium">{isAutoPlaying ? '暂停' : '自动跟读'}</span>
        </button>

        {/* 重读 */}
        <button
          onClick={resetPage}
          className="flex flex-col items-center justify-center gap-1 w-16 h-14 rounded-2xl bg-green-500 text-white active:bg-green-600 transition shadow-sm"
        >
          <RotateCcw className="w-5 h-5" />
          <span className="text-[10px] font-medium">重读</span>
        </button>
      </div>
    </div>
  );
};

export default ReadAlongMode;
